import { DotMarkingContainer } from "encamp-shared/src/waste/dotMarkingGroup";
import { hazardLabelPageClasses } from "encamp-shared/src/waste/pngExportLoss";
import { WasteClassification } from "generated-graphql/graphql";

/** The label-content fields the summary sentence reads. */
export type LabelSummaryContent = {
  classification?: WasteClassification | null;
  dotHazardClass?: string | null;
  dotSubsidiaryHazardClass?: string | null;
  physicalState?: string | null;
  qrUrl?: string | null;
};

const HAZARDOUS_CLASSIFICATIONS: WasteClassification[] = [
  WasteClassification.RcraHazardous,
  WasteClassification.RcraAcutelyHazardous,
];

/**
 * What the generated label actually contains, in a sentence.
 *
 * Every clause is conditional on the data being there, because the label is
 * assembled from the container's own record: a nonhazardous stream gets no
 * "HW" wording, a container that prints no regulated label page gets no
 * hazard-label clause,
 * and a label that has never been generated has no "Updated" date. Saying
 * otherwise would describe an artifact the reader isn't holding.
 */
export function labelSummarySentence(
  content: LabelSummaryContent | null | undefined,
  container: Omit<DotMarkingContainer, "physicalState">
): string {
  const isHazardous =
    content?.classification != null && HAZARDOUS_CLASSIFICATIONS.includes(content.classification);

  const parts: string[] = [isHazardous ? "Compliant HW label" : "Compliant waste label"];
  // The same page list the PDF prints from: none for class 7, class 1, a
  // class missing its division, or a placarded bulk container.
  const pages = content
    ? hazardLabelPageClasses({ ...container, physicalState: content.physicalState }, content)
    : [];
  if (content?.dotHazardClass && pages.includes(content.dotHazardClass)) {
    parts.push(`DOT Class ${content.dotHazardClass} label`);
  }
  if (content?.dotSubsidiaryHazardClass && pages.includes(content.dotSubsidiaryHazardClass)) {
    parts.push(`Class ${content.dotSubsidiaryHazardClass} subsidiary label`);
  }
  if (content?.qrUrl) {
    parts.push("QR");
  }

  return `${parts.join(" + ")}, built from this container's data.`;
}
